'use client';

import * as React from 'react';
import {
  BadgeCheck,
  Bell,
  Brain,
  Briefcase,
  Captions,
  Check,
  Clock,
  Download,
  FileText,
  Globe,
  Languages,
  Layout,
  LucideIcon,
  Mic,
  Mic2,
  MicOff,
  MonitorPlay,
  RefreshCw,
  Rocket,
  Shield,
  Sliders,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Upload,
  Users,
  Video,
  VoicemailIcon,
  Wand2,
  Zap
} from 'lucide-react';

import { cn } from '@workspace/ui/lib/utils';

import { StepsProps } from '~/types/step-type';
import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';

const iconMap: Record<string, LucideIcon> = {
  upload: Upload,
  fileText: FileText,
  mic: Mic,
  video: Video,
  download: Download,
  zap: Zap,
  clock: Clock,
  sparkles: Sparkles,
  mic2: Mic2,
  users: Users,
  sliders: Sliders,
  globe: Globe,
  captions: Captions,
  voicemailIcon: VoicemailIcon,
  rocket: Rocket,
  layout: Layout,
  monitorPlay: MonitorPlay,
  bell: Bell,
  briefcase: Briefcase,
  smartphone: Smartphone,
  brain: Brain,
  languages: Languages,
  micOff: MicOff,
  refreshCw: RefreshCw,
  shield: Shield,
  badgeCheck: BadgeCheck,
  wand2: Wand2,
  slidersHorizontal: SlidersHorizontal
};

export default function HowItWorksSection({
  badge,
  title,
  description,
  isSecondary,
  id,
  steps
}: StepsProps): React.JSX.Element {
  return (
    <section
      className="bg-background relative container"
      id={id}
    >
      <SiteHeading
        badge={badge}
        title={title}
        isSecondary={isSecondary}
        description={description}
      />

      {/* Steps */}
      <div className="space-y-12 sm:space-y-16 lg:space-y-24">
        {steps?.map((step) => (
          <div
            key={step?.id}
            id={step?.id}
            className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            {/* Text Content */}
            <BlurFade
              inView
              delay={0.1}
              className={cn(step.reverse && 'lg:order-2')}
            >
              <div
                className={cn(
                  'flex mb-4 sm:mb-6',
                  step?.subHeading ? 'items-start' : 'items-center'
                )}
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-videomule-green rounded-xl flex items-center justify-center text-white mr-3 sm:mr-4 shrink-0">
                  {(() => {
                    const Icon = step?.logo ? iconMap[step.logo] : undefined;
                    return Icon ? (
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" />
                    ) : null;
                  })()}
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
                  {step?.heading}
                  {step?.subHeading && (
                    <span className="text-lg sm:text-xl lg:text-2xl font-medium text-muted-foreground">
                      {' '}
                      ({step?.subHeading})
                    </span>
                  )}
                </h3>
              </div>
              <p className="text-muted-foreground text-base lg:text-lg mb-6 sm:mb-8 leading-relaxed">
                {step?.description}
              </p>
              <div className="space-y-2 sm:space-y-3">
                {step?.points?.map((point, pointIndex) => (
                  <BlurFade
                    key={pointIndex}
                    inView
                    delay={0.2 + pointIndex * 0.1}
                  >
                    <div className="flex items-center">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-videomule-green mr-2 sm:mr-3 shrink-0" />
                      <span className="text-muted-foreground text-sm sm:text-base">
                        {point}
                      </span>
                    </div>
                  </BlurFade>
                ))}
              </div>
            </BlurFade>

            {/* Mockup */}
            <BlurFade
              inView
              delay={0.2}
              className={`${step.reverse ? 'lg:order-1' : ''} bg-card rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-border`}
            >
              {step.mockup}
            </BlurFade>
          </div>
        ))}
      </div>
      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
    </section>
  );
}
